import { createFileRoute } from "@tanstack/react-router";
import { hourlyMinimumFaq } from "@/lib/booking-minimums";
import VehiclePage, { vehicleHead, FLEET_LINKS, WHICH_VEHICLE, type VehicleConfig } from "@/components/VehiclePage";
import hero from "@/assets/jet-style-sprinter-white-interior-captain-seats.jpg?format=webp&quality=72&w=1400&imagetools";
import mid from "@/assets/sprinter-starlight-blue.jpg?format=webp&quality=72&w=1400&imagetools";
import g1 from "@/assets/sprinter-exec-orange.jpg?format=webp&quality=72&w=1000&imagetools";
import g2 from "@/assets/mercedes-benz-stadium-sprinter-limo-party-bus.jpg?format=webp&quality=72&w=1000&imagetools";
import g3 from "@/assets/sprinter-starlight-blue.jpg?format=webp&quality=72&w=1000&imagetools";
import yellow from "@/assets/sprinter-exec-yellow.jpg?format=webp&quality=72&w=1200&imagetools";
import red from "@/assets/sprinter-exec-red.jpg?format=webp&quality=72&w=1000&imagetools";
import jetStairs from "@/assets/aviation/private-jet-stairs.jpg?format=webp&quality=70&w=800&imagetools";

const cfg: VehicleConfig = {
  path: "/jet-sprinter-atlanta",
  title: "Jet Sprinter Atlanta | First-Class Sprinter for Up to 7 Guests",
  description:
    "Chauffeured Jet Sprinter in Atlanta: starlight ceiling, first-class captain seats, cinema screen and refreshment bar for up to 7. Ideal for FBO and VIP transfers. Call (678) 431-7222.",
  serviceType: "Jet Sprinter",
  eyebrow: "Our Fleet • Jet Sprinter",
  h1: "Jet Sprinter in Atlanta",
  intro:
    "You've landed. Now settle into first-class captain seating for up to 7, with a starlight ceiling, cinema screen and refreshment bar. My chauffeurs handle the next part of your trip.",
  heroImage: hero,
  heroAlt: "Jet Sprinter interior with white first-class captain seats in Atlanta",
  quickFacts: [
    { value: "Up to 7", label: "Guests, all in captain seats" },
    { value: "From $250", label: "Per hour, custom quotes" },
    { value: "Starlight", label: "Ceiling with mood lighting" },
    { value: "We own", label: "Every vehicle in our fleet" },
  ],
  introImage: yellow,
  introImageAlt: "Jet Sprinter cabin with white captain seats and warm ceiling lighting",
  midImage: mid,
  midAlt: "Jet Sprinter starlight ceiling with blue lighting",
  aboutTitle: "A Quiet Finish to Your Flight",
  about: [
        "The Jet Sprinter gives your small group room to settle in, with an aviation-inspired cabin and individual captain seats.",
        "We arrange private aviation pickups at PDK, Fulton County and Cobb County, along with VIP and small-group travel.",
      ],
  featuresTitle: "Jet Sprinter Features",
  features: [
    "Up to 7 passengers",
    "First-class captain seats",
    "Starlight ceiling",
    "Cinema screen",
    "Refreshment bar with a built-in cooler",
    "High-speed WiFi",
    "USB charging ports",
  ],
  bookingBands: [
    { title: "Text Me Your Arrival Plans", body: "Send me your date, pickup and group size. I'll check the Jet Sprinter and send a written quote." },
    { title: "Let's Plan Your Jet Sprinter Trip", body: "Sprinters start at $250/hour. Text me your itinerary for a custom written quote." },
  ],
  bestFor: ["Private jet and FBO pickups", "VIP and celebrity guests", "Executive small groups", "Anniversaries and date nights", "Airport transfers", "Special occasions"],
  details: [
    {
      title: "Built for the Private Aviation Handoff",
      body: [
        "The Jet Sprinter is our most-requested vehicle for private aviation pickups. Send us the airport, the FBO and your arrival or departure time when you book, and we plan the pickup around it.",
        "Track your chauffeur live in our app, so you always know when they'll pull up.",
      ],
      list: [
        "DeKalb-Peachtree Airport (PDK)",
        "Fulton County Airport, Charlie Brown Field (FTY)",
        "Cobb County International Airport (RYY)",
        "Hartsfield-Jackson Atlanta International (ATL)",
      ],
      image: red,
      imageAlt: "Jet Sprinter with white captain seats, tables and red accent lighting",
    },
    WHICH_VEHICLE,
  ],
  gallery: [
    { src: hero, alt: "Jet Sprinter white captain seats", caption: "First-class captain seats" },
    { src: g2, alt: "Jet Sprinter starlight ceiling with red lighting", caption: "Starlight ceiling" },
    { src: g1, alt: "Jet Sprinter with orange accent lighting", caption: "Adjustable accent lighting" },
    { src: red, alt: "Jet Sprinter cabin with red lighting and tables", caption: "Tables between the captain seats" },
    { src: yellow, alt: "Jet Sprinter cabin with warm lighting", caption: "Warm lighting for a quiet ride" },
    { src: g3, alt: "Jet Sprinter starlight ceiling with blue lighting", caption: "Mood lighting in any color" },
  ],
  faqImage: g1,
  faqImageAlt: "Jet Sprinter with orange accent lighting",
  faqs: [
    { q: "How many people fit in the Jet Sprinter?", a: "Up to 7 passengers, all in first-class captain seats." },
    { q: "How is the Jet Sprinter different from the Limo Style Sprinter?", a: "The Jet Sprinter seats up to 7 in individual captain seats with a cinema screen, built for comfort and conversation. The Limo Style Sprinter seats up to 14 in wraparound lounge seating with a private bar, built for celebrations." },
    { q: "Can the Jet Sprinter pick us up at a private jet terminal?", a: "Yes. It is our most-requested vehicle for private aviation pickups at PDK, Fulton County, Cobb County and the other Atlanta-area airports." },
    { q: "Can we drink onboard?", a: "Guests 21 and over may bring their own drinks. We provide glassware and ice; we do not sell or provide alcohol." },
    { q: "How much does the Jet Sprinter cost?", a: "Sprinters start from $250/hour. Custom quotes are given upon request based on your date and itinerary." },
    hourlyMinimumFaq({ transfers: true }),
  ],
  related: [FLEET_LINKS.executive, FLEET_LINKS.limo, FLEET_LINKS.suburban, { to: "/private-jet-car-service-atlanta", title: "Private Jet Car Service", desc: "Chauffeured pickups at every Atlanta FBO.", image: jetStairs }],
  ctaTitle: "Text Me About Your Jet Sprinter",
  ctaBody: "Send me your date, pickup and group size. I'll check availability myself and send your custom quote in writing.",
};

export const Route = createFileRoute("/jet-sprinter-atlanta")({
  head: () => vehicleHead(cfg),
  component: JetSprinterPage,
});

function JetSprinterPage() {
  return <VehiclePage cfg={cfg} />;
}
